import React, { useEffect, useState } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import ProjectService from 'service/ProjectService';
import { Link } from 'react-router-dom';
import { hasProjectAccess } from '../../helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const Project: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isRunningFilter, setIsRunningFilter] = useState<boolean | null>(null);

  const projectService = ProjectService();

  const canCreate = hasProjectAccess(profile, 'create');
  const canUpdate = hasProjectAccess(profile, 'update');
  const canDelete = hasProjectAccess(profile, 'delete');

  // Animated nav-pills indicator
  const { navRef: statusNavRef, indicatorStyle: statusIndicatorStyle } = useNavPillsIndicator(isRunningFilter);

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await projectService.projectList(token, searchTerm, currentPage, pageSize, isRunningFilter);
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err) {
      console.error(err);
      window.alert('Terjadi kesalahan saat mengambil data project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, isRunningFilter]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleIsRunningFilter = (value: boolean | null) => {
    setIsRunningFilter(value);
    setCurrentPage(1);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Project</h5>
            <div><p>TransMore Project</p></div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4 bg-white">
            <div className="mb-3 d-flex justify-content-between align-items-center flex-nowrap gap-2 overflow-auto" style={{ whiteSpace: 'nowrap' }}>
              <form className="d-flex align-items-center flex-nowrap" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120, height: 48 }}
                />
                <button type="submit" className="px-4 btn-primary btn">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex align-items-center gap-2" style={{ flex: 1, justifyContent: 'flex-end' }}>
                <div className="align-self-stretch d-flex align-items-stretch">
                  <div ref={statusNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                    <div
                      className="nav-pill-indicator"
                      style={statusIndicatorStyle}
                    />
                    <button
                      type='button'
                      className={`h-100 nav-link ${isRunningFilter === null ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(null)}
                    >
                      All
                    </button>
                    <button
                      type='button'
                      className={`h-100 nav-link ${isRunningFilter === true ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(true)}
                    >
                      Running
                    </button>
                    <button
                      type='button'
                      className={`h-100 nav-link ${isRunningFilter === false ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(false)}
                    >
                      Stopped
                    </button>
                  </div>
                </div>

                {canCreate && (
                  <Link to="/project/0" className="text-decoration-none tm-text-primary">
                    <button className="btn btn-primary">
                      <span className="me-2">New Project</span>
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </Link>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Target / Progress</th>
                  <th>Is Running</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>
                          {item.name}
                          {(item.reportingDasboard || item.reportingDashboard) && (
                            <span className="ms-2 text-primary" data-bs-toggle="tooltip" title="Available on MoreSight" style={{ cursor: 'pointer' }}>
                              <i className="fa-solid fa-chart-pie" style={{ fontSize: '0.85rem' }}></i>
                            </span>
                          )}
                        </td>
                        <td>{item.clientName}</td>
                        <td>{item.startDate ? item.startDate.substring(0, 10) : ''}</td>
                        <td>{item.endDate ? item.endDate.substring(0, 10) : ''}</td>
                        <td>
                          <div style={{ minWidth: '150px' }}>
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                              <span>Target: <strong>{item.target}</strong></span>
                              <span className="text-muted fw-bold">
                                {item.target > 0 ? Math.round(((item.resultCount?.approved ?? 0) / item.target) * 100) : 0}%
                              </span>
                            </div>
                            <div className="progress mb-2" style={{ height: '6px' }} title={`Approved: ${item.resultCount?.approved ?? 0} of ${item.target}`}>
                              <div
                                className="progress-bar bg-primary"
                                role="progressbar"
                                style={{ width: `${Math.min(item.target > 0 ? Math.round(((item.resultCount?.approved ?? 0) / item.target) * 100) : 0, 100)}%` }}
                                aria-valuenow={item.target > 0 ? Math.round(((item.resultCount?.approved ?? 0) / item.target) * 100) : 0}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              ></div>
                            </div>
                            {item.resultCount ? (
                              <div className="fw-bold text-nowrap" style={{ fontSize: '0.8rem' }}>
                                <span title="Total">{item.resultCount.total}</span>
                                {" - "}
                                <span className="text-success" title="Approved">{item.resultCount.approved}</span>
                                {" - "}
                                <span className="text-warning" title="Done">{item.resultCount.done}</span>
                                {" - "}
                                <span className="text-danger" title="Rejected">{item.resultCount.rejected}</span>
                                {" - "}
                                <span className="text-secondary" title="Archived">{item.resultCount.archived}</span>
                              </div>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '0.8rem' }}>0 - 0 - 0 - 0 - 0</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${item.isRunning ? 'bg-success' : 'bg-secondary'}`}>
                            {item.isRunning ? 'Running' : 'Stopped'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex">
                            <Link to={`/project/detail/${item.id}`}>
                              <button className="border-0 btn btn-outline-info btn-small-compact" title="View Detail">
                                <i className="fa-solid fa-eye"></i>
                              </button>
                            </Link>

                            {canUpdate && (
                              <Link to={`/project/${item.id}`}>
                                <button className="border-0 btn btn-outline-warning btn-small-compact ms-2" title="Edit Project">
                                  <i className="fa-solid fa-edit"></i>
                                </button>
                              </Link>
                            )}

                            {canDelete && (
                              <button className="border-0 btn btn-outline-danger btn-small-compact ms-2" title="Delete Project">
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center">Tidak ada data</td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Project;
